/**
 * BindingAuthCard — DEVICE-DETAIL-MANAGE-1F.
 *
 * Seção "Vínculo e autorização" da página Detalhes: mostra o estado atual
 * (autorização, cliente/vínculo, QR serial, protegido) e expõe as ações
 * administrativas que JÁ têm backend pronto, com confirmação e RBAC corretos:
 *
 *   - Remover vínculo  → DELETE /api/admin/devices/bindings/{qr_serial}
 *                        (deleteDeviceBinding) — CAP_FLEET_WRITE; NÃO apaga device/estoque.
 *   - Autorizar        → POST /api/admin/devices/{id}/authorize   (PLATFORM-ONLY)
 *   - Bloquear         → POST /api/admin/devices/{id}/block       (PLATFORM-ONLY)
 *   - Revogar          → POST /api/admin/devices/{id}/revoke      (PLATFORM-ONLY)
 *
 * authorize/block/revoke exigem confirmação forte (useCriticalConfirm) e só
 * aparecem para platform admin (isPlatformAdmin). Backend = fonte da verdade dos
 * guards (platform-only + critical confirm + denylist/protegido). NÃO cria comando
 * de relay/reboot/OTA; NÃO toca HMAC/MAC/secret/device_id. Reset de Fábrica está
 * FORA desta fase (de propósito).
 */
import React, { useState } from "react";

import { authorizeDevice, blockDevice, deleteDeviceBinding, revokeDevice } from "../lib/api.js";
import { CAP, isPlatformAdmin } from "../lib/rbac.js";
import Can from "./Can.jsx";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";

const STATUS_LABEL = { authorized: "Autorizado", pending: "Pendente", blocked: "Bloqueado" };
const STATUS_CLS = {
  authorized: "bg-noc-ok/15 text-noc-ok border-noc-ok/40",
  pending: "bg-noc-warn/15 text-noc-warn border-noc-warn/40",
  blocked: "bg-noc-err/15 text-noc-err border-noc-err/40",
};

function friendlyErr(e) {
  const s = e?.status;
  if (s === 401) return "Sessão expirada — faça login novamente.";
  if (s === 403) return "Sem permissão (ação platform-only ou perfil sem fleet:write) ou confirmação recusada.";
  if (s === 404) return "Device ou vínculo não encontrado.";
  if (s === 409) return "Conflito: o device já está nesse estado (409).";
  if (s === 422) return `Dado inválido: ${e?.message || "verifique os campos."}`;
  return e?.message || "Falha na ação.";
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-[11px] uppercase tracking-wide text-noc-faint">{k}</span>
      <span className={"text-[13px] text-noc-text text-right " + (mono ? "font-mono" : "")}>{v}</span>
    </div>
  );
}

export default function BindingAuthCard({ data, notify, onChanged }) {
  const confirmCritical = useCriticalConfirm();
  const d = data?.device || {};
  const b = data?.binding || {};
  const inv = data?.inventory || {};
  const deviceId = d.device_id;
  const status = d.authorization_status || "pending";
  const qr = inv.qr_serial || b.qr_serial || null;
  const customer = b.customer_id || d.customer_code || null;
  const protectedDev = !!d.ota_protected;
  const platformAdmin = isPlatformAdmin();

  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function open(a) { setAction(a); setReason(""); setError(null); }
  function close() { if (!busy) { setAction(null); setError(null); } }

  async function doConfirm() {
    if (!action) return;
    setError(null);
    try {
      if (action.critical) {
        const secret = await confirmCritical({
          actionLabel: action.title,
          target: { kind: "single", deviceId },
          impact: action.impact,
        });
        if (secret == null) return; // cancelou no diálogo de senha
        setBusy(true);
        await action.run({ reason: reason.trim() || undefined, confirm: secret });
      } else {
        setBusy(true);
        await action.run({});
      }
      if (notify) notify("ok", action.successMsg);
      setBusy(false);
      setAction(null);
      onChanged && onChanged();
    } catch (e) {
      setError(friendlyErr(e));
      setBusy(false);
    }
  }

  const ACTIONS = {
    unbind: {
      title: "Remover vínculo",
      impact: "Desfaz o vínculo QR ↔ cliente/IXC. NÃO apaga o device nem o item de estoque; apenas desvincula.",
      danger: true, critical: false,
      successMsg: "Vínculo removido.",
      run: () => deleteDeviceBinding(qr),
    },
    authorize: {
      title: "Autorizar device",
      impact: "Libera o device para operação: passa a aparecer em listas/mapa e a receber comandos/OTA/templates/alertas.",
      danger: false, critical: true,
      successMsg: "Device autorizado.",
      run: (a) => authorizeDevice(deviceId, a),
    },
    block: {
      title: "Bloquear device",
      impact: "Bloqueia o device: impede operação e autorização (some das listas operacionais). Não bloqueia o heartbeat/telemetria.",
      danger: true, critical: true,
      successMsg: "Device bloqueado.",
      run: (a) => blockDevice(deviceId, a),
    },
    revoke: {
      title: "Revogar autorização",
      impact: "Remove a autorização (volta para a fila pendente). O device sai das listas operacionais até ser autorizado de novo.",
      danger: true, critical: true,
      successMsg: "Autorização revogada.",
      run: (a) => revokeDevice(deviceId, a),
    },
  };

  const btn = (cls) =>
    "h-9 px-3 rounded-lg text-xs font-semibold border transition-all hover:-translate-y-0.5 " + cls;
  const safe = "border-noc-border text-noc-text hover:border-noc-accent/50";
  const danger = "border-noc-err/30 text-noc-err bg-noc-err/10";

  return (
    <div className="glass-card-pad space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-noc-muted">
        Vínculo e autorização
      </div>

      {protectedDev && (
        <div className="text-[12px] bg-noc-warn/10 border border-noc-warn/30 text-noc-warn
                        rounded-md px-3 py-2">
          🔒 Device PROTEGIDO (produção/gold/canário). O backend mantém os guards; ações
          podem ser recusadas (403).
        </div>
      )}

      {/* Estado atual */}
      <div className="space-y-0.5">
        <Row k="Autorização" v={
          <span className={"px-2 py-0.5 rounded-md border text-[11px] font-semibold "
            + (STATUS_CLS[status] || STATUS_CLS.pending)}>
            {STATUS_LABEL[status] || status}
          </span>} />
        <Row k="Cliente / vínculo" v={customer || "Sem vínculo"} />
        <Row k="QR serial" v={qr || "—"} mono />
        <Row k="device_id" v={deviceId || "—"} mono />
      </div>

      {/* Ações */}
      <div className="flex flex-wrap gap-2 pt-1">
        <Can capability={CAP.FLEET_WRITE}>
          {qr && (
            <button className={btn(danger)} onClick={() => open(ACTIONS.unbind)}>
              Remover vínculo
            </button>
          )}
        </Can>
        {platformAdmin && status !== "authorized" && (
          <button className={btn(safe)} onClick={() => open(ACTIONS.authorize)}>Autorizar</button>
        )}
        {platformAdmin && status !== "blocked" && (
          <button className={btn(danger)} onClick={() => open(ACTIONS.block)}>Bloquear</button>
        )}
        {platformAdmin && status === "authorized" && (
          <button className={btn(danger)} onClick={() => open(ACTIONS.revoke)}>Revogar</button>
        )}
      </div>
      {!platformAdmin && (
        <div className="text-[11px] text-noc-faint">
          Autorizar/Bloquear/Revogar são ações de plataforma (somente super admin).
        </div>
      )}

      {/* Modal de confirmação */}
      {action && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={close}>
          <div className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-md p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[15px] font-semibold text-noc-text">{action.title}</h2>
            <div className={"text-[12px] rounded-md px-3 py-2 border "
              + (action.danger ? "bg-noc-err/10 border-noc-err/30 text-noc-err"
                               : "bg-noc-warn/10 border-noc-warn/30 text-noc-warn")}>
              {action.impact}{protectedDev && " 🔒 Device PROTEGIDO."}
            </div>
            <div className="space-y-0.5">
              <Row k="device_id" v={deviceId || "—"} mono />
              {qr && <Row k="QR serial" v={qr} mono />}
              {customer && <Row k="Cliente" v={customer} />}
            </div>
            {action.critical && (
              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-noc-faint">Motivo (opcional)</label>
                <input className="w-full bg-noc-bg border border-noc-border rounded-md px-2 py-1.5
                                  text-[13px] text-noc-text focus:border-noc-accent outline-none"
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="ex.: provisionamento aprovado" />
              </div>
            )}
            {error && (
              <div className="text-[12px] bg-noc-err/10 border border-noc-err/30 text-noc-err
                              rounded-md px-3 py-2">{error}</div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button className="btn-ghost" onClick={close} disabled={busy}>Cancelar</button>
              <button className={action.danger ? "btn-danger" : "btn-primary"}
                onClick={doConfirm} disabled={busy}>
                {busy ? "Processando…" : action.title}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
